import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

export default function Blog() {
  const [posts, setPosts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [category, setCategory] = React.useState("");

  const fetchPosts = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = category ? { category } : {};
      const r = await api.get("/blog", { params });
      setPosts(r.data || []);
    } finally {
      setLoading(false);
    }
  }, [category]);

  React.useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const categories = React.useMemo(() => {
    const set = new Set(posts.map((p) => p.category));
    return Array.from(set);
  }, [posts]);

  return (
    <div className="bg-section-glow" data-testid="blog-page">
      <section className="pt-20 pb-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <h1 className="font-display lowercase font-bold text-5xl sm:text-6xl tracking-tighter" data-testid="blog-heading">
            playbooks for <span className="text-blue-500">modern operators.</span>
          </h1>
          <p className="mt-5 text-gray-400 max-w-2xl">
            short, tactical reads on ai, automation and websites that book
            themselves.
          </p>

          {categories.length > 0 && (
            <div className="mt-8 flex flex-wrap gap-2" data-testid="blog-categories">
              <button
                onClick={() => setCategory("")}
                className={`px-4 py-1.5 rounded-full text-xs border transition ${
                  category === ""
                    ? "bg-blue-600 border-blue-500 text-white"
                    : "border-white/10 text-gray-300 hover:border-blue-500/60"
                }`}
                data-testid="blog-category-all"
              >
                all
              </button>
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setCategory(c)}
                  className={`px-4 py-1.5 rounded-full text-xs border transition ${
                    category === c
                      ? "bg-blue-600 border-blue-500 text-white"
                      : "border-white/10 text-gray-300 hover:border-blue-500/60"
                  }`}
                  data-testid={`blog-category-${c}`}
                >
                  {c.toLowerCase()}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          {loading ? (
            <div className="flex justify-center py-24 text-gray-400">
              <Loader2 className="animate-spin" />
            </div>
          ) : posts.length === 0 ? (
            <p className="text-center text-gray-500 py-24">no posts yet.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((p, i) => (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ delay: i * 0.05 }}
                >
                  <Link
                    to={`/blog/${p.slug}`}
                    className="kyv-card block overflow-hidden group"
                    data-testid={`blog-card-${p.slug}`}
                  >
                    {p.image_url ? (
                      <img
                        src={p.image_url}
                        alt={p.title}
                        className="w-full aspect-[16/10] object-cover group-hover:scale-[1.02] transition duration-500"
                      />
                    ) : (
                      <div className="w-full aspect-[16/10] bg-blue-500/10" />
                    )}
                    <div className="p-6">
                      <div className="flex items-center gap-2 text-xs text-gray-400">
                        <span className="text-blue-400">{p.category?.toLowerCase()}</span>
                        <span>·</span>
                        <span>
                          {new Date(p.created_at).toLocaleDateString("en", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </span>
                      </div>
                      <h3 className="mt-3 font-display lowercase font-bold text-xl tracking-tight group-hover:text-blue-300 transition">
                        {p.title.toLowerCase()}
                      </h3>
                      {p.excerpt && (
                        <p className="mt-2 text-sm text-gray-400 line-clamp-3">
                          {p.excerpt}
                        </p>
                      )}
                      <span className="mt-4 inline-flex items-center gap-2 text-blue-400 text-sm">
                        read <ArrowRight size={14} />
                      </span>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
